/**
 * Global Command Drawer.
 *
 * Subscribes to /api/events/stream (SSE) and renders every command's
 * lifecycle. Surfaces in-flight count as a chip in the navbar so the
 * operator is never in the dark about whether a click reached the box.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Zap, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import api from '../services/api';
import { FleetCommand, FleetEvent } from '../types';

const STATUS_COLOR: Record<string, string> = {
  queued: 'border-fg-muted text-fg-muted',
  sending: 'border-status-warning text-status-warning',
  received: 'border-status-buffered text-status-buffered',
  in_progress: 'border-status-buffered text-status-buffered',
  done: 'border-status-online text-status-online',
  failed: 'border-status-error text-status-error',
  superseded: 'border-fg-muted text-fg-muted',
  cancelled: 'border-fg-muted text-fg-muted',
  timed_out: 'border-status-error text-status-error',
};

const LIVE_STATUSES = new Set(['queued', 'sending', 'received', 'in_progress']);
const TERMINAL_STATUSES = new Set(['done', 'failed', 'superseded', 'cancelled', 'timed_out']);

function relativeTime(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const delta = Math.max(0, (Date.now() - t) / 1000);
  if (delta < 1) return 'now';
  if (delta < 60) return `${Math.round(delta)}s ago`;
  if (delta < 3600) return `${Math.round(delta / 60)}m ago`;
  return `${Math.round(delta / 3600)}h ago`;
}

interface CommandDrawerProps {
  open: boolean;
  onClose: () => void;
  commands: FleetCommand[];
  onCancel: (cmd: FleetCommand) => Promise<void>;
}

export const CommandDrawer: React.FC<CommandDrawerProps> = ({
  open,
  onClose,
  commands,
  onCancel,
}) => {
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-md">
        <SheetHeader className="border-b border-border p-4">
          <SheetTitle className="text-accent">Commands</SheetTitle>
        </SheetHeader>

        {commands.length === 0 ? (
          <div className="p-8 text-center text-fg-secondary">
            <p>No recent commands.</p>
            <p className="mt-1 text-sm">Live updates appear here as you control devices.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            {commands.map((cmd) => (
              <div
                key={cmd.uuid}
                className="flex flex-col gap-1 border-b border-border/50 px-4 py-3"
              >
                <div className="flex w-full items-center gap-2">
                  <span className="font-mono text-sm font-medium">{cmd.kind}</span>
                  <Badge
                    variant="outline"
                    className={cn('font-mono', STATUS_COLOR[cmd.status] || '')}
                  >
                    {cmd.status}
                  </Badge>
                  <div className="flex-1" />
                  {cmd.status === 'queued' && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="text-status-error"
                            onClick={() => onCancel(cmd)}
                            aria-label="Cancel queued command"
                          >
                            <XCircle />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel queued command</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
                <div className="font-mono text-xs text-fg-secondary">
                  {cmd.device_id} · {cmd.uuid.slice(0, 8)} · attempts {cmd.attempts}
                </div>
                <div className="mt-1 flex gap-3 text-xs text-fg-muted">
                  <span>created {relativeTime(cmd.created_at)}</span>
                  {cmd.acked_at && <span>acked {relativeTime(cmd.acked_at)}</span>}
                  {cmd.completed_at && <span>done {relativeTime(cmd.completed_at)}</span>}
                </div>
                {cmd.error && (
                  <div className="font-mono text-xs text-status-error">{cmd.error}</div>
                )}
                {cmd.last_progress && Object.keys(cmd.last_progress).length > 0 && (
                  <div className="font-mono text-xs text-status-buffered">
                    progress: {JSON.stringify(cmd.last_progress)}
                  </div>
                )}
                {cmd.result && Object.keys(cmd.result).length > 0 && (
                  <div className="font-mono text-xs text-fg-secondary">
                    result: {JSON.stringify(cmd.result)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

/**
 * State container + chip trigger. Mounted once at the App root so all pages
 * see live command updates without re-subscribing.
 */
export const CommandDrawerHost: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [commands, setCommands] = useState<FleetCommand[]>([]);

  useEffect(() => {
    let cancelled = false;
    api
      .listFleetCommands(false, 100)
      .then((rows) => {
        if (!cancelled) setCommands(rows);
      })
      .catch(() => {
        // Endpoint may 404 on older backends; surface nothing rather than crash.
      });
    const unsubscribe = api.subscribeFleetEvents(
      (event: FleetEvent) => {
        if (event.type === 'command_update') {
          setCommands((prev) => {
            const idx = prev.findIndex((c) => c.uuid === event.command.uuid);
            if (idx >= 0) {
              const next = prev.slice();
              next[idx] = event.command;
              return next;
            }
            return [event.command, ...prev].slice(0, 200);
          });
        }
      },
      () => {
        // SSE will auto-reconnect via EventSource itself.
      },
    );
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const inFlight = useMemo(
    () => commands.filter((c) => LIVE_STATUSES.has(c.status)).length,
    [commands],
  );

  const failedRecently = useMemo(
    () =>
      commands.some((c) => {
        if (c.status !== 'failed' && c.status !== 'timed_out') return false;
        const t = c.completed_at ? new Date(c.completed_at).getTime() : 0;
        return Date.now() - t < 60_000;
      }),
    [commands],
  );

  const cancel = async (cmd: FleetCommand) => {
    try {
      await api.cancelFleetCommand(cmd.device_id, cmd.uuid);
    } catch (err) {
      console.warn('cancel failed', err);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        title={
          inFlight > 0
            ? `${inFlight} device command${inFlight === 1 ? '' : 's'} queued or sending — click to open the command drawer`
            : 'Device command history'
        }
        className={cn(
          'ml-1 whitespace-nowrap font-mono',
          failedRecently
            ? 'text-status-error'
            : inFlight > 0
              ? 'text-status-warning'
              : 'text-fg-secondary',
        )}
      >
        <Zap />
        {inFlight > 0 ? `${inFlight} in flight` : 'commands'}
      </Button>
      <CommandDrawer
        open={open}
        onClose={() => setOpen(false)}
        commands={commands}
        onCancel={cancel}
      />
    </>
  );
};

export { TERMINAL_STATUSES, LIVE_STATUSES };
export default CommandDrawerHost;
